import React, { useEffect, useState } from 'react'
import Logo from './Logo'
import Search from './Search'
import MobileHeader from './Mobileheader'
import useMobile from '../hooks/useMobile';
import { useDispatch as useReduxDispatch, useSelector as useReduxSelector } from 'react-redux'
import { fetchStores } from '../store/locationSlice'
import { FaRegCircleUser } from "react-icons/fa6"
import { FaShoppingCart } from "react-icons/fa"
import { FaHeart } from 'react-icons/fa'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import SummaryApi from '../common'
import { toast } from 'react-toastify'
import { setUserDetails } from '../store/userSlice'
import ROLE from '../common/role'
import { selectCartCount } from '../store/cartSlice'
import CategoryNav from './CategoryNav'

const Header = () => {
  const dispatch = useDispatch()
  const rdispatch = useReduxDispatch()
  const [menuDisplay, setMenuDisplay] = useState(false)
  const [ isMobile ] = useMobile()
  const location = useLocation()
  const isSearchPage = location.pathname === "/search"
  const navigate = useNavigate()
  const user = useSelector(state => state?.user?.user)
  // Single selector reads server count for authed users and a memoized guestItems sum
  // for everyone else — replaces the prior split between a Context `cartProductCount`
  // and a local `guestCartCount` useState driven by a `guest-cart-updated` CustomEvent.
  const cartCount = useSelector(selectCartCount)


  const handleLogout = async () => {
    try {
      const fetchData = await fetch(SummaryApi.logout_user.url, {
        method: SummaryApi.logout_user.method,
        credentials: 'include'
      })

      const data = await fetchData.json()

      if (data.success) {
        toast.success(data.message)
        dispatch(setUserDetails(null))
        navigate("/")
      }

      if (data.error) {
        toast.error(data.message)
      }
    } catch (error) {
      toast.error('Logout failed')
    }
  }

 

  // Trigger uses a real <button> (with aria-haspopup + aria-expanded) so screen readers
  // announce the popup state and keyboard users can toggle with Space/Enter. The previous
  // markup was a <div onClick> which AT didn't recognize as interactive.
  const UserMenu = () => (
    user?._id && (
      <button
        type='button'
        className='text-3xl cursor-pointer relative flex justify-center bg-transparent border-0 p-0'
        aria-haspopup='menu'
        aria-expanded={menuDisplay}
        aria-label='User menu'
        onClick={() => setMenuDisplay(prev => !prev)}
      >
        {user?.profilePic ? (
          <img src={user.profilePic} className='w-10 h-10 rounded-full' alt={user.name} />
        ) : (
          <FaRegCircleUser />
        )}
      </button>
    )
  )

  // Close the menu on Escape — common dropdown convention.
  useEffect(() => {
    if (!menuDisplay) return
    const onKey = (e) => { if (e.key === 'Escape') setMenuDisplay(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuDisplay])

  const DropdownMenu = () => (
    menuDisplay && (
<div role='menu' className='absolute right-0 top-full mt-2 bg-white z-50 h-fit p-2 shadow-lg rounded'>
        <nav>
          {user?._id && (
            <Link 
              to="/my-orders" 
              className='whitespace-nowrap block hover:bg-slate-100 p-2' 
              onClick={() => setMenuDisplay(prev => !prev)}
            >
              My Orders
            </Link>
          )}
          <Link 
            to="/preorders" 
            className='whitespace-nowrap block hover:bg-slate-100 p-2' 
            onClick={() => setMenuDisplay(prev => !prev)}
          >
            Pre-orders
          </Link>
          <Link 
            to="/track-order" 
            className='whitespace-nowrap block hover:bg-slate-100 p-2' 
            onClick={() => setMenuDisplay(prev => !prev)}
          >
            Track Order
          </Link>
          {user?.role === ROLE.VENDOR && (
            <Link
              to="/vendor-portal"
              className='whitespace-nowrap block hover:bg-slate-100 p-2'
              onClick={() => setMenuDisplay(prev => !prev)}
            >
              Seller Portal
            </Link>
          )}
          {user?.role === ROLE.GENERAL && (
            <Link
              to="/become-a-vendor"
              className='whitespace-nowrap block hover:bg-slate-100 p-2'
              onClick={() => setMenuDisplay(prev => !prev)}
            >
              Sell on UrbanAI
            </Link>
          )}
          {(user?.role === ROLE.SUPER_ADMIN || user?.role === ROLE.ADMIN || user?.role === ROLE.SUB_ADMIN) && (
            <>
              <Link
                to="/admin-panel/all-products"
                className='whitespace-nowrap block hover:bg-slate-100 p-2'
                onClick={() => setMenuDisplay(prev => !prev)}
              >
                Admin Panel
              </Link>
              <Link 
                to="/tickets/scanner" 
                className='whitespace-nowrap block hover:bg-slate-100 p-2' 
                onClick={() => setMenuDisplay(prev => !prev)}
              >
                Ticket Scanner
              </Link>
            </>
          )}
        </nav>
      </div>
    )
  )

  // Load stores on mount if flag enabled
  useEffect(() => {
    const flag = String(process.env.REACT_APP_MULTI_STORE_ENABLED || '').toLowerCase()
    const enabled = flag === 'true' || flag === '1' || flag === 'yes'
    if (enabled) {
      try { rdispatch(fetchStores()) } catch (_) {}
    }
  }, [rdispatch])

  const CartIcon = () => {
    const count = Number(cartCount || 0)
    const displayCount = count > 99 ? '99+' : count
    return (
      <Link to={"/cart"} className='text-2xl relative' aria-label="Cart">
        <span><FaShoppingCart/></span>
        {count > 0 && (
          <div className='absolute -top-2 -right-2 bg-premium-600 text-white h-5 min-w-[1.25rem] px-1 rounded-full flex items-center justify-center text-[10px] leading-none font-semibold'>
            {displayCount}
          </div>
        )}
      </Link>
    )
  }

  const WishlistIcon = () => {
    const wishlistCount = useReduxSelector(s => (s?.wishlist?.items || []).length)
    const count = Number(wishlistCount || 0)
    const displayCount = count > 99 ? '99+' : count
    return (
      <Link to="/wishlist" className='text-2xl relative' aria-label="Wishlist">
        <span><FaHeart/></span>
        {count > 0 && (
          <div className='absolute -top-2 -right-2 bg-premium-600 text-white h-5 min-w-[1.25rem] px-1 rounded-full flex items-center justify-center text-[10px] leading-none font-semibold'>
            {displayCount}
          </div>
        )}
      </Link>
    )
  }

  const AuthButton = () => (
    user?._id ? (
      <button 
        onClick={handleLogout} 
        className='btn bg-premium-600 hover:bg-premium-700 text-white'
      >
        Logout
      </button>
    ) : (
      <Link 
        to="/login" 
        className='btn bg-premium-600 hover:bg-premium-700 text-white'
      >
        Login
      </Link>
    )
  )

  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header className={`sticky top-0 z-40 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/70 transition-shadow ${scrolled ? 'shadow-sm border-b border-slate-200/60' : ''}`}> 
      {/* Main header row */}
      <div className='h-24 lg:h-20 flex items-center'>
        <div className='container mx-auto flex items-center px-4 justify-between'>
          {!(isSearchPage && isMobile) && (
            <div className='w-full flex items-center justify-between gap-4'>
              {/* Logo */}
              <div className='flex items-center'>
                <Link to='/' className='flex items-center'>
                  <Logo w={240} h={70} />
                </Link>
              </div>

              {/* Mobile controls */}
              <div className='lg:hidden'>
                <MobileHeader />
              </div>

              {/* Desktop search */}
              <div className='hidden lg:block min-w-[420px] flex-1 max-w-2xl'>
                <Search />
              </div>

              {/* Right actions */}
              <div className='flex items-center gap-7'>
                <div className='relative flex justify-center'>
                  <UserMenu />
                  <DropdownMenu />
                </div>
                <CartIcon />
                <WishlistIcon />
                <AuthButton />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Category nav — desktop only. Previously rendered at md+ which left tablets in an
          awkward state (showing the mobile header AND the desktop category bar at the same
          time). The mobile header carries its own navigation, so the category bar now
          appears only when the desktop search/header is also visible (lg+). */}
      {!(isSearchPage && isMobile) && (
        <div className='hidden lg:block'>
          <CategoryNav />
        </div>
      )}
    </header>
  )
}

export default Header